/**
 * sellerTokenStore — in-memory JWT holders for the seller-admin and
 * seller-user portals.
 *
 * Phase-5 XSS hardening: neither of these tokens is written to
 * localStorage or sessionStorage anymore. The refresh token lives in an
 * httpOnly cookie (see /api/auth/{admin|user}/refresh) which JS can't
 * touch. On page reload we silently hit the refresh endpoint to
 * re-hydrate the access token from the cookie.
 *
 * Two independent stores (one for admin, one for user) so an admin
 * signed in on the same browser as an end-user doesn't accidentally
 * inherit the wrong role.
 */

const makeStore = (label) => {
  let token = null;
  let user = null;
  const subs = new Set();

  const emit = () => subs.forEach((fn) => {
    try { fn(token); } catch (e) {
      // Don't let a bad listener starve the others.
      console.warn(`[${label}] subscriber threw`, e);
    }
  });

  return {
    getAccessToken: () => token,
    getUser: () => user,
    setAccessToken: (t) => { token = t || null; emit(); },
    setUser: (u) => { user = u || null; },
    setSession: (t, u) => { token = t || null; user = u || null; emit(); },
    clear: () => { token = null; user = null; emit(); },
    subscribe: (fn) => { subs.add(fn); return () => subs.delete(fn); },
    getAuthHeader: () => (token ? `Bearer ${token}` : null),
  };
};

export const adminTokenStore = makeStore('adminTokenStore');
export const userTokenStore = makeStore('userTokenStore');

// One-time scrub — nuke any pre-Phase-5 localStorage token mirrors so
// existing users don't keep a stale XSS-readable copy hanging around.
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('user_token');
    // NOTE: keep `user_data` for now — the seller portal reads it for UX
    // fields (name/company) that aren't sensitive. The XSS-critical piece
    // is the JWT, which lives above only.
  }
} catch (e) {
  void e; // private-mode storage; swallow
}
